import { Avatar } from "@jutro/ui";
import { OptionWithSideElements } from "@jutro/ui/dist/Select/tools";
import dayjs from "dayjs";
import { envVars } from "envvars";
import {
  EdmMedicalAppShiftStatus,
  EdmMedicalAppShiftWithoutVisitFragment,
  EdmMedicalAppVisitType,
  EdmNonWorkingPeriod,
} from "lib/graphql/megaSchema";
import { avatarConfig } from "lib/tools/httpConfigs";
import {
  StaffOption,
  getStaffRole,
  isMedicalAppShift,
  isNonWorkingPeriod,
} from "views/StaffSchedule/StaffScheduleCreator/tools";
import {
  leaveTypeLabelMap,
  visitTypeLabelMap,
} from "views/StaffSchedule/tools";

type Props = {
  shift: EdmMedicalAppShiftWithoutVisitFragment | EdmNonWorkingPeriod;
  shiftLocalization: string;
  staff: OptionWithSideElements<StaffOption>;
};

export const BlockDetailsTooltip = ({
  shift,
  staff,
  shiftLocalization,
}: Props) => {
  const { endedAt, startFrom, comment } = shift;
  const { label, value } = staff;

  return (
    <div className="text-jutro-new-warm-gray-700 w-[420px] text-base">
      <div className="bg-jutro-new-warm-gray-100 rounded-t-lg px-2 py-1">
        <h5>{getTooltipTitle(shift)}</h5>
      </div>

      <div className="flex flex-col gap-2 p-2">
        <div className="flex items-start justify-between">
          <FieldWrapper
            title={label}
            content={getStaffRole(value.genre, value.npwz)}
          />
          <Avatar
            type="photo"
            imgSrc={`${avatarConfig[envVars.REACT_APP_CONFIG]}/${value.id}`}
            size="lg"
          />
        </div>

        {isMedicalAppShift(shift) && (
          <FieldWrapper title="Typy wizyt" content={shift.visitTypes} />
        )}

        {isNonWorkingPeriod(shift) && (
          <FieldWrapper
            title="Powód"
            content={leaveTypeLabelMap[shift.leaveType]}
          />
        )}

        {isMedicalAppShift(shift) && (
          <FieldWrapper
            title="Status personelu"
            content={getStaffStatus(shift.status)}
          />
        )}

        <FieldWrapper
          title="Zakres godzinowy"
          content={`${dayjs(startFrom).format("HH:mm")} - ${dayjs(endedAt).format("HH:mm")}`}
        />

        {isMedicalAppShift(shift) && (
          <FieldWrapper title="Lokalizacja" content={shiftLocalization} />
        )}

        {comment && <FieldWrapper title="Komentarz" content={comment} />}

        {isMedicalAppShift(shift) && shift.createdBy.name && (
          <FieldWrapper title="Dodane przez" content={shift.createdBy.name} />
        )}
      </div>
    </div>
  );
};

const FieldWrapper = ({
  title,
  content,
}: {
  title: string;
  content: string | EdmMedicalAppVisitType[];
}) => {
  return (
    <div className="flex flex-col">
      <span className="text-jutro-new-warm-gray-700 font-bold">{title}</span>
      {Array.isArray(content) ? (
        content.map((o) => visitTypeLabelMap[o]).join(", ")
      ) : (
        <span>{content}</span>
      )}
    </div>
  );
};

const getTooltipTitle = (
  shift: EdmMedicalAppShiftWithoutVisitFragment | EdmNonWorkingPeriod,
) => {
  if (isMedicalAppShift(shift)) {
    return "Szczegóły zmiany";
  }

  if (shift.leaveType.includes("UNPAID_HOLIDAY")) {
    return "Blok czasowy bezpłatny";
  }

  return "Blok czasowy płatny";
};

const getStaffStatus = (shiftStatus: EdmMedicalAppShiftStatus) => {
  if (shiftStatus === "ACCEPTING_NFZ") {
    return "Zgłoszony/a do NFZ";
  }

  if (shiftStatus === "ACCEPTING_PRIV") {
    return "Obsługuje PRIV";
  }

  return "Zgłoszony/a do NFZ, Obsługuje PRIV";
};
